import { nip19 } from 'nostr-tools';

import { fetchJsonResilient, fetchTextResilient } from '@/lib/resilientFetch';

/**
 * Web author discovery (NIP-84 attribution helper).
 *
 * Web pages can declare their author's Nostr identity with
 * `<link rel="me" href="nostr:nprofile1…" />` (the Mastodon-style rel=me
 * convention). When a highlight of a web page knows the author's pubkey,
 * zaps on that highlight can pay the person who wrote the words.
 */

const HEX_64 = /^[0-9a-f]{64}$/i;

/** Extract a pubkey from a `nostr:npub1…/nprofile1…` URI. */
export function pubkeyFromNostrUri(href: string): string | null {
  if (!href.startsWith('nostr:')) return null;
  try {
    const decoded = nip19.decode(href.slice('nostr:'.length));
    if (decoded.type === 'npub') return decoded.data;
    if (decoded.type === 'nprofile') return decoded.data.pubkey;
  } catch {
    return null;
  }
  return null;
}

/** All rel=me-declared Nostr pubkeys on a page (empty if none/unreachable). */
export function parseRelMePubkeys(html: string): string[] {
  // DOMParser never executes scripts — safe for untrusted HTML.
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const elements = doc.querySelectorAll('link[rel~="me"][href^="nostr:"], a[rel~="me"][href^="nostr:"]');

  const pubkeys: string[] = [];
  for (const el of elements) {
    const pubkey = pubkeyFromNostrUri(el.getAttribute('href') ?? '');
    if (pubkey && !pubkeys.includes(pubkey)) pubkeys.push(pubkey);
  }
  return pubkeys;
}

/**
 * Fetch a web page and look for a declared Nostr identity:
 * `<link rel="me" href="nostr:…">` in the head, or an
 * `<a rel="me" href="nostr:…">` verification link in the body.
 * Returns the author's pubkey hex, or null.
 */
export async function discoverWebAuthor(url: string): Promise<string | null> {
  const html = await fetchTextResilient(url);
  if (!html) return null;

  return parseRelMePubkeys(html)[0] ?? null;
}

/** Resolve a NIP-05 identifier (name@domain) to a pubkey. */
export async function resolveNip05(identifier: string): Promise<string | null> {
  const [name, domain] = identifier.split('@');
  if (!name || !domain) return null;

  const url = `https://${domain}/.well-known/nostr.json?name=${encodeURIComponent(name)}`;
  const json = (await fetchJsonResilient(url, 6000)) as { names?: Record<string, string> } | null;
  if (!json) return null;

  const pubkey = json.names?.[name] ?? json.names?.[name.toLowerCase()];
  if (typeof pubkey === 'string' && HEX_64.test(pubkey)) {
    return pubkey.toLowerCase();
  }
  return null;
}

/**
 * Domain-level NIP-05 hint (`_@domain`) for a page. A *suggestion* only —
 * the domain owner isn't necessarily the author on multi-author sites, so
 * this must never be auto-credited without user confirmation.
 */
export async function suggestDomainIdentity(url: string): Promise<string | null> {
  try {
    const domain = new URL(url).hostname;
    if (!domain) return null;
    return await resolveNip05(`_@${domain}`);
  } catch {
    return null;
  }
}

/**
 * Resolve a manually entered author identity to a pubkey.
 * Accepts npub/nprofile (with or without nostr:), bare hex, or NIP-05.
 */
export async function resolveAuthorInput(input: string): Promise<string | null> {
  let value = input.trim();
  if (!value) return null;

  if (value.startsWith('nostr:')) {
    value = value.slice('nostr:'.length);
  }

  if (/^(npub|nprofile)1[023456789acdefghjklmnpqrstuvwxyz]+$/i.test(value)) {
    try {
      const decoded = nip19.decode(value);
      if (decoded.type === 'npub') return decoded.data;
      if (decoded.type === 'nprofile') return decoded.data.pubkey;
    } catch {
      return null;
    }
    return null;
  }

  if (HEX_64.test(value)) {
    return value.toLowerCase();
  }

  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
    return resolveNip05(value);
  }

  return null;
}
